// Live Client Activity — real-time feed of meaningful client actions across all files (replaces the old "Needs attention" queues).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Activity, Flame, FileUp, PenLine, UserRound, CheckCircle2, MonitorSmartphone, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 10000;

const FILTERS = [
  ['all', 'All activity', Activity],
  ['active', 'Active now', Radio],
  ['portal', 'Client Portal', MonitorSmartphone],
  ['documents', 'Documents', FileUp],
  ['esign', 'E-Sign', PenLine],
  ['idv', 'ID Verification', UserRound],
  ['completed', 'Completed', CheckCircle2],
];
const KIND = {
  portal: { Icon: MonitorSmartphone, cls: 'bg-indigo-50 text-indigo-700' },
  documents: { Icon: FileUp, cls: 'bg-sky-50 text-sky-700' },
  esign: { Icon: PenLine, cls: 'bg-violet-50 text-violet-700' },
  idv: { Icon: UserRound, cls: 'bg-amber-50 text-amber-700' },
  completed: { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700' },
};

export function timeAgo(iso, now = Date.now()) {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 45) return 'Just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return d === 1 ? 'Yesterday' : `${d} days ago`;
}

export function ActiveNow({ className = '' }) {
  return <span className={`inline-flex items-center gap-1 rounded-full bg-emerald-500 px-1.5 py-[2px] text-[9px] font-black uppercase tracking-wider text-white ${className}`} data-testid="lca-active-now"><span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Active now</span>;
}

export function useLiveActivity(headers, filter) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const seen = useRef(new Set());
  const [fresh, setFresh] = useState(new Set());
  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/client-activity/live`, { headers, params: { filter } });
      const nf = new Set();
      if (seen.current.size) d.feed.forEach((f) => { if (!seen.current.has(f.id)) nf.add(f.id); });
      d.feed.forEach((f) => seen.current.add(f.id));
      setFresh(nf); setData(d); setErr(false);
    } catch { setErr(true); }
  }, [headers, filter]);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);
  return { data, err, fresh, reload: load };
}

function FilterBar({ filter, setFilter, data }) {
  const countFor = (k) => (k === 'active' ? data?.active_count : k === 'all' ? null : data?.counts?.[k]) || 0;
  const current = FILTERS.find(([k]) => k === filter);
  return (
    <div className="px-1 pb-2.5" data-testid="lca-filters">
      <div className="grid grid-cols-7 gap-1 rounded-xl bg-slate-100 p-1">
        {FILTERS.map(([k, l, Icon]) => {
          const on = filter === k; const c = countFor(k);
          return (
            <button key={k} type="button" onClick={() => setFilter(k)} title={l} aria-label={l} aria-pressed={on} data-testid={`lca-filter-${k}`}
              className={`relative grid h-9 place-items-center rounded-lg transition-[background-color,color,box-shadow] duration-150 ${on ? 'bg-white text-[#15133A] shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-800 hover:bg-white/60'}`}>
              <Icon className={`w-4 h-4 ${k === 'active' && c ? 'text-emerald-500' : ''}`} />
              {c > 0 && <span className={`absolute -top-1 -right-0.5 min-w-[16px] rounded-full px-1 text-[9px] font-black leading-4 text-center ${k === 'active' ? 'bg-emerald-500 text-white' : on ? 'bg-[#15133A] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'}`}>{c > 99 ? '99+' : c}</span>}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 px-1 text-[11px] text-slate-500" data-testid="lca-filter-label">Showing <span className="font-bold text-slate-800">{current?.[1]}</span>{filter !== 'all' && countFor(filter) ? <span> · {countFor(filter)} {filter === 'active' ? 'client' : 'event'}{countFor(filter) === 1 ? '' : 's'}</span> : null}</p>
    </div>
  );
}

function MostActive({ list, onOpen }) {
  if (!list?.length) return null;
  return (
    <div className="mb-3 rounded-xl bg-slate-50 p-2.5" data-testid="lca-most-active">
      <p className="px-1 pb-1.5 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5"><Flame className="w-3 h-3 text-orange-500" /> Most active right now</p>
      <div className="space-y-0.5">
        {list.map((c, i) => (
          <button key={c.app_id} type="button" onClick={() => onOpen(c.to)} data-testid={`lca-top-${c.app_id}`} className="w-full flex items-center gap-2 rounded-lg px-1.5 py-1.5 text-left hover:bg-white transition-colors">
            <span className={`h-5 w-5 shrink-0 rounded-md text-[10px] font-black grid place-items-center ${i === 0 ? 'bg-[#15133A] text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>{i + 1}</span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-bold text-slate-900">{c.client}{c.hot && <Flame className="inline w-3 h-3 ml-1 text-orange-500 -mt-0.5" data-testid={`lca-hot-${c.app_id}`} />}</span>
              <span className="block truncate text-[10.5px] text-slate-500">{c.n24} action{c.n24 === 1 ? '' : 's'} · {timeAgo(c.last_at)}</span>
            </span>
            {c.active_now ? <ActiveNow /> : <span className="text-[10px] font-black text-slate-400">{c.score}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Row({ f, fresh, onOpen }) {
  const k = KIND[f.kind] || KIND.portal;
  return (
    <button type="button" onClick={() => onOpen(f.to)} data-testid={`lca-row-${f.id}`}
      className={`w-full flex items-start gap-3 rounded-xl px-2 py-2.5 text-left transition-colors hover:bg-slate-50 ${fresh ? 'lca-fresh' : ''}`}>
      <span className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg ${k.cls}`}><k.Icon className="w-4 h-4" /></span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5"><span className="truncate text-[13px] font-bold text-slate-900">{f.client}</span>{f.hot && <Flame className="w-3 h-3 shrink-0 text-orange-500" title="Hot file — several actions in the last 30 minutes" />}</span>
        <span className="block truncate text-[12px] text-slate-600 mt-px">{f.label}</span>
        <span className="mt-1 flex items-center gap-1.5 text-[10.5px] text-slate-400">{f.active_now ? <ActiveNow /> : timeAgo(f.at)}</span>
      </span>
    </button>
  );
}

export default function LiveClientActivity({ headers, bare = false }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const { data, err, fresh } = useLiveActivity(headers, filter);
  const onOpen = (to) => navigate(to);
  const feed = useMemo(() => data?.feed || [], [data]);
  return (
    <div className={bare ? 'p-1' : 'rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_1px_2px_rgba(15,19,58,0.04),0_12px_28px_-16px_rgba(15,19,58,0.18)]'} data-testid="lca-panel">
      <style>{`.lca-fresh{animation:lcaIn .6s ease-out}@keyframes lcaIn{0%{background:#ecfdf5;transform:translateY(-4px);opacity:.2}100%{background:transparent;transform:none;opacity:1}}`}</style>
      <div className={`flex items-center ${bare ? 'justify-end' : 'justify-between'} px-2 pt-1 pb-2`}>
        {!bare && <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] text-slate-400"><Activity className="w-3.5 h-3.5 text-slate-300" /> Live client activity</p>}
        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600" data-testid="lca-live"><Radio className={`w-3 h-3 ${err ? 'text-rose-500' : 'animate-pulse'}`} /> {err ? 'Offline' : 'Live'}</span>
      </div>
      {data && (data.active_count > 0 || data.hot_count > 0) && (
        <div className="mx-2 mb-2 flex flex-wrap gap-1.5 text-[10.5px] font-bold">
          {data.active_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700" data-testid="lca-active-count"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> {data.active_count} active now</span>}
          {data.hot_count > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-orange-700" data-testid="lca-hot-count"><Flame className="w-3 h-3" /> {data.hot_count} hot file{data.hot_count === 1 ? '' : 's'}</span>}
        </div>
      )}
      <MostActive list={data?.most_active} onOpen={onOpen} />
      <FilterBar filter={filter} setFilter={setFilter} data={data} />
      <div className="max-h-[520px] overflow-auto -mx-0.5 space-y-1 border-t border-slate-100 pt-1.5" data-testid="lca-feed">
        {!data && <p className="px-3 py-6 text-center text-xs text-slate-400">Loading activity…</p>}
        {data && feed.length === 0 && <p className="px-3 py-6 text-center text-xs text-slate-400" data-testid="lca-empty">{filter === 'active' ? 'No clients in their portal right now' : 'No client activity in the last 7 days'}</p>}
        {feed.map((f) => <Row key={f.id} f={f} fresh={fresh.has(f.id)} onOpen={onOpen} />)}
      </div>
    </div>
  );
}
